import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { RISK_LABELS, RISK_COLORS, PRIORITY_LABELS, PRIORITY_COLORS, STATUS_LABELS, CAPTURE_SITUATION_LABELS, formatDate } from "@/lib/labels";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sparkles, HeartPulse, Rat, ClipboardList, AlertTriangle } from "lucide-react";

const BAND_CLS = { verde: "text-emerald-400", amarelo: "text-amber-400", laranja: "text-orange-400", vermelho: "text-red-400" };
const RISK_DOT = { verde: "bg-emerald-500", amarelo: "bg-amber-400", laranja: "bg-orange-500", vermelho: "bg-red-500", cinza: "bg-slate-500" };

export function RoomHistoryDialog({ roomId, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (roomId) {
      setData(null);
      api.get(`/electric-rooms/${roomId}/history`).then(({ data: d }) => setData(d)).catch(() => onClose());
    }
  }, [roomId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={!!roomId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto scrollbar-thin" data-testid="room-history-dialog">
        <DialogHeader><DialogTitle className="font-heading">Histórico da Sala Elétrica</DialogTitle></DialogHeader>
        {!data ? (
          <div className="py-10 flex justify-center"><div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>
        ) : (
          <div className="space-y-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="font-heading font-semibold">{data.room.name}{data.room.code ? ` · ${data.room.code}` : ""}</p>
                <p className="text-xs text-muted-foreground">{data.room.client_name} › {data.room.unit_name}</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-center" data-testid="room-health-score">
                  <p className={`font-heading text-3xl font-bold ${BAND_CLS[data.health.band]}`}>{data.health.score}</p>
                  <p className="text-[9px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><HeartPulse size={10} /> Índice de saúde</p>
                </div>
                <Badge variant="outline" className={RISK_COLORS[data.room.last_risk || "cinza"]}>{RISK_LABELS[data.room.last_risk || "cinza"]}</Badge>
              </div>
            </div>

            {data.risk_timeline.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Evolução do risco</p>
                <div className="flex items-end gap-1.5 flex-wrap" data-testid="risk-timeline">
                  {data.risk_timeline.map((t, i) => (
                    <div key={i} className="flex flex-col items-center gap-1" title={`${formatDate(t.date)} — ${RISK_LABELS[t.risk]} (${t.nc} NC, ${t.rodents} roedores)`}>
                      <span className={`h-4 w-4 rounded-full ${RISK_DOT[t.risk]}`} />
                      <span className="text-[8px] text-muted-foreground">{String(t.date).slice(5, 10)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.captures.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><Rat size={12} className="text-rose-400" /> Capturas de roedores ({data.captures.length})</p>
                <div className="space-y-1.5">
                  {data.captures.map((c) => (
                    <div key={c.id} className="rounded-md border border-border px-3 py-2 flex items-center justify-between gap-2 text-sm" data-testid={`history-capture-${c.id}`}>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{c.quantity}x {c.species_name}</p>
                        <p className="text-[11px] text-muted-foreground">{c.technician_name} · {formatDate(c.captured_at)}{c.gps?.lat ? " · GPS ✓" : ""}</p>
                      </div>
                      <Badge variant="outline" className={c.situation === "vivo" ? "bg-amber-500/15 text-amber-400 border-amber-500/30" : "bg-slate-500/15 text-slate-400 border-slate-500/30"}>
                        {CAPTURE_SITUATION_LABELS[c.situation]}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><ClipboardList size={12} /> Inspeções ({data.inspections.length})</p>
              {data.inspections.length === 0 ? <p className="text-sm text-muted-foreground">Nenhuma inspeção registrada.</p> : (
                <div className="space-y-1.5">
                  {data.inspections.map((i) => (
                    <div key={i.id} className="rounded-md border border-border px-3 py-2 flex items-center justify-between gap-2 text-sm">
                      <div className="min-w-0">
                        <p className="text-[11px] text-muted-foreground">{i.inspector_name} · {formatDate(i.finished_at)}</p>
                        <p className="text-xs">{i.nc_count} NC{i.rodents_total > 0 ? ` · ${i.rodents_total} roedor(es)` : ""}{i.droppings?.found ? " · fezes" : ""}{i.traces?.length ? ` · ${i.traces.length} vestígio(s)` : ""}{i.occurrence_protocols?.length ? ` · ${i.occurrence_protocols.join(", ")}` : ""}</p>
                      </div>
                      <Badge variant="outline" className={RISK_COLORS[i.risk_level]}>{RISK_LABELS[i.risk_level]}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {data.occurrences.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><AlertTriangle size={12} /> Ocorrências geradas ({data.occurrences.length})</p>
                <div className="space-y-1.5">
                  {data.occurrences.map((o) => (
                    <div key={o.id} className="rounded-md border border-border px-3 py-2 flex items-center justify-between gap-2 text-sm">
                      <div className="min-w-0">
                        <p className="font-mono text-xs">{o.protocol}</p>
                        <p className="text-xs text-muted-foreground truncate">{o.type_name}</p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Badge variant="outline" className={PRIORITY_COLORS[o.priority]}>{PRIORITY_LABELS[o.priority]}</Badge>
                        <Badge variant="outline">{STATUS_LABELS[o.status]}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Md({ text }) {
  return (
    <div className="space-y-1.5 text-sm">
      {String(text).split("\n").map((ln, i) => {
        const t = ln.trim();
        if (!t) return null;
        if (t.startsWith("## ")) return <h4 key={i} className="font-heading font-semibold text-sm text-primary mt-3">{t.slice(3).replace(/\*\*/g, "")}</h4>;
        if (t.startsWith("# ")) return <h4 key={i} className="font-heading font-semibold mt-3">{t.slice(2).replace(/\*\*/g, "")}</h4>;
        if (t.startsWith("- ") || t.startsWith("* ") || t.startsWith("• ")) return <p key={i} className="pl-3 text-muted-foreground">• {t.slice(2).replace(/\*\*/g, "")}</p>;
        return <p key={i} className="text-muted-foreground">{t.replace(/\*\*/g, "").replace(/^#+\s*/, "")}</p>;
      })}
    </div>
  );
}

export function AiAnalysisDialog({ open, onOpenChange, clientId, unitId }) {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/electric-rooms/ai-analysis", {
        client_id: clientId !== "todos" ? clientId : null,
        unit_id: unitId !== "todas" ? unitId : null,
      });
      setAnalysis(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto scrollbar-thin" data-testid="ai-analysis-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Sparkles size={17} className="text-primary" /> Análise Técnica Inteligente — Salas Elétricas
          </DialogTitle>
        </DialogHeader>
        {!analysis ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              A IA analisará as capturas de roedores, checklists, riscos e o índice de saúde das salas dos filtros atuais para gerar: tendência das capturas, salas críticas, possíveis rotas de acesso, recomendações de inspeção estrutural e ações preventivas.
            </p>
            <Button className="w-full" onClick={generate} disabled={loading} data-testid="ai-analysis-generate-btn">
              <Sparkles size={15} className="mr-2" /> {loading ? "Analisando dados de campo..." : "Gerar Análise IA"}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div data-testid="ai-analysis-content"><Md text={analysis.content} /></div>
            <p className="text-[11px] text-muted-foreground border-t border-border pt-3">
              Análise gerada por IA em {formatDate(analysis.created_at)} por solicitação de {analysis.created_by}. As sugestões são apoio à decisão — a palavra final é do técnico/supervisor.
            </p>
            <Button variant="outline" className="w-full" onClick={generate} disabled={loading} data-testid="ai-analysis-regenerate-btn">
              {loading ? "Analisando..." : "Gerar nova análise"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
